import type { ComponentProps, FC, ReactNode } from 'react'

import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

const CONTROL = 'w-full rounded-lg border border-input bg-card px-3.5 text-base text-foreground shadow-[0_1px_0_rgb(14_27_48/0.03)] transition-[border-color,box-shadow] outline-none placeholder:text-slate-400 focus:border-primary focus:ring-3 focus:ring-primary/15 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-muted-foreground aria-[invalid=true]:border-red-500 aria-[invalid=true]:ring-red-500/15'

interface FieldProps {
    label: ReactNode
    htmlFor?: string
    hint?: ReactNode
    error?: string | null
    optional?: boolean
    children: ReactNode
    className?: string
}

export const Field: FC<FieldProps> = ({
    label,
    htmlFor,
    hint,
    error,
    optional = false,
    children,
    className
}): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <label htmlFor={htmlFor} className='flex items-baseline gap-1.5 text-sm font-semibold text-foreground'>
                {label}

                {optional && (
                    <span className='text-xs font-medium text-muted-foreground'>
                        Optional
                    </span>
                )}
            </label>

            {children}

            {error
                ? (
                    <p className='text-[13px] font-medium text-red-700' role='alert'>
                        {error}
                    </p>
                )
                : hint && (
                    <p className='text-[13px] text-muted-foreground'>
                        {hint}
                    </p>
                )}
        </div>
    )
}

interface InputProps extends ComponentProps<'input'> {
    leading?: ReactNode
    trailing?: ReactNode
}

export const Input: FC<InputProps> = ({
    className,
    leading,
    trailing,
    ...props
}): ReactNode => {
    if (!leading && !trailing) return <input className={cn(CONTROL, 'h-12', className)} {...props} />

    return (
        <div className='relative'>
            {leading && (
                <span className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-muted-foreground'>
                    {leading}
                </span>
            )}

            <input
                className={cn(CONTROL, 'h-12', leading && 'pl-11', trailing && 'pr-12', className)}
                {...props}
            />

            {trailing && (
                <span className='absolute inset-y-0 right-0 flex items-center pr-1.5'>
                    {trailing}
                </span>
            )}
        </div>
    )
}

export const Textarea: FC<ComponentProps<'textarea'>> = ({ className, ...props }): ReactNode => {
    return <textarea className={cn(CONTROL, 'min-h-28 resize-y py-3 leading-relaxed', className)} {...props} />
}

export const Select: FC<ComponentProps<'select'>> = ({ className, children, ...props }): ReactNode => {
    return (
        <div className='relative'>
            <select className={cn(CONTROL, 'h-12 appearance-none pr-10', className)} {...props}>
                {children}
            </select>

            <ChevronDown className='pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
        </div>
    )
}
